import { Calendar, Clock, MapPin, User, Trophy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getPhotoUrl } from "@/utils/photoUrl";

const Volleyball = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20"/><path d="M2 12h20"/>
  </svg>
);

const TEAM_COLORS = { 1: "blue", 2: "green", 3: "orange" };
const getTeamColor = (n) => TEAM_COLORS[n] || "blue";

function PlayerAvatar({ player, size = "w-7 h-7" }) {
  return (
    <div className={`${size} rounded-full overflow-hidden bg-muted flex-shrink-0`}>
      {player.photo_url ? (
        <img src={getPhotoUrl(player.photo_url)} alt="" className="w-full h-full object-cover" />
      ) : (
        <User className="w-full h-full p-1 text-muted-foreground" />
      )}
    </div>
  );
}

function TeamPlayers({ players, playerId, size = "w-7 h-7", textSize = "text-xs" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {players.map((p) => (
        <div key={p.id || p.name} className="flex items-center gap-2">
          <PlayerAvatar player={p} size={size} />
          <span className={`${textSize} truncate ${p.id === playerId ? "text-[#CCFF00] font-bold" : "text-foreground"}`}>
            {p.first_name || p.name?.split(" ")[0] || "Jogador"}
          </span>
        </div>
      ))}
    </div>
  );
}

function GamedayCard({ gameday, playerId, formatDate, t }) {
  const color = getTeamColor(gameday.team_number);

  return (
    <div className="p-4 bg-muted/50 rounded-lg border border-border/50">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#CCFF00]" />
          <span className="text-foreground font-medium">{formatDate(gameday.date || gameday.created_at)}</span>
        </div>
        {gameday.team_number && (
          <Badge className={`bg-${color}-500/20 text-${color}-400 border-${color}-500/30`}>
            Time {gameday.team_number}
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-3 text-sm">
        {gameday.location && (
          <div className="flex items-center gap-1 text-foreground">
            <MapPin className="h-4 w-4 text-[#CCFF00]" />
            <span>{gameday.location}</span>
          </div>
        )}
        {(gameday.start_time || gameday.end_time) && (
          <div className="flex items-center gap-1 text-foreground">
            <Clock className="h-4 w-4 text-[#CCFF00]" />
            <span>{gameday.start_time || "--:--"} - {gameday.end_time || "--:--"}</span>
          </div>
        )}
      </div>

      {gameday.teammates?.length > 0 && (
        <div className={`p-3 rounded-lg bg-${color}-500/10 border border-${color}-500/30`}>
          <h4 className={`font-bold text-sm mb-2 text-${color}-400`}>
            {t("playerDashboard.yourTeam") || "Seu Time"} - Time {gameday.team_number}
          </h4>
          <TeamPlayers players={gameday.teammates} playerId={playerId} />
        </div>
      )}

      {gameday.all_teams?.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
            {t("playerDashboard.viewAllTeams") || "Ver todos os times"}
          </summary>
          <div className="mt-3 space-y-3">
            {gameday.all_teams.map((team) => {
              const tc = getTeamColor(team.team_number);
              return (
                <div key={team.team_number} className={`p-3 rounded-lg bg-${tc}-500/10 border border-${tc}-500/30`}>
                  <h5 className={`font-bold text-xs mb-2 text-${tc}-400`}>
                    Time {team.team_number} {team.team_number === gameday.team_number && "(Voce)"}
                  </h5>
                  <TeamPlayers players={team.players || []} playerId={playerId} size="w-5 h-5" textSize="text-xs" />
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

export default function TeamsTab({ playerGamedays, playerId, formatDate, t }) {
  return (
    <Card className="bg-card/50 border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Trophy className="h-5 w-5 text-[#CCFF00]" />
          {t("playerDashboard.myTeams") || "Meus Times"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {playerGamedays.length === 0 ? (
          <div className="text-center py-8">
            <Volleyball className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t("playerDashboard.noTeamsYet") || "Voce ainda nao foi escalado em nenhum time"}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {playerGamedays.map((gameday, idx) => (
              <GamedayCard key={gameday.id || idx} gameday={gameday} playerId={playerId} formatDate={formatDate} t={t} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
